import React, { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import i18n from '../i18n';
// ★ 2026-08-30 (code review) — sanificazione input HTML esterno
import { sanitizeHtml } from '../lib/sanitize';
import { logger } from '../lib/logger';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Bold, Italic, Underline, List, ListOrdered, Link2, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Heading3, Eraser,
  Code, Quote, Undo2, Redo2,
} from 'lucide-react';

/**
 * RichEmailEditor — Editor rich-text minimale in stile Gmail per il corpo
 * email delle proposte. Basato su `contenteditable` + `document.execCommand`
 * (deprecato ma ancora funzionante ovunque nei browser moderni).
 *
 * ★ 2026-08-10 (Fase 35) — Alternativa alla textarea HTML "raw".
 * Props:
 *   - value: HTML string
 *   - onChange: (html) => void
 *   - placeholder?: string
 *   - minHeight?: css value (default 240px)
 *   - testId?: string prefix for data-testid
 *
 * ★ Fase 8.U — ref imperativo: `insertText(text)` inserisce testo nel punto
 * in cui si trova (o si trovava) il cursore dentro l'editor. La posizione
 * viene memorizzata ad ogni selectionchange interna, così l'inserimento
 * funziona anche dopo aver cliccato su bottoni/popover esterni.
 *
 * ★ Fase 8.V — prop `highlightFields`: evidenzia visivamente i placeholder
 * {{campo}} tramite CSS Custom Highlight API (nessuna modifica al DOM/HTML
 * salvato; degradazione elegante sui browser che non la supportano).
 */
const FIELD_HL_RE = /\{\{\s*[a-zA-Z0-9_]{1,40}\s*\}\}/g;

const RichEmailEditor = forwardRef(function RichEmailEditor({
  value,
  onChange,
  placeholder = 'Scrivi il corpo dell\'email…',
  minHeight = '240px',
  testId = 'rich-email-editor',
  highlightFields = false,
}, ref) {
  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const highlightRafRef = useRef(null);
  const [showHtml, setShowHtml] = useState(false);
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [imagePopoverOpen, setImagePopoverOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [rawHtml, setRawHtml] = useState(value || '');

  // ★ Fase 8.V — evidenzia i {{campi}} con la CSS Custom Highlight API.
  // Non tocca il DOM: crea Range sui text node e li registra in CSS.highlights.
  const updateHighlights = useCallback(() => {
    if (!highlightFields) return;
    if (typeof CSS === 'undefined' || !('highlights' in CSS)) return; // fallback silenzioso
    if (highlightRafRef.current) cancelAnimationFrame(highlightRafRef.current);
    highlightRafRef.current = requestAnimationFrame(() => {
      const root = editorRef.current;
      const name = `${testId}-fields`;
      if (!root) { CSS.highlights.delete(name); return; }
      const ranges = [];
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();
      while (node) {
        const text = node.textContent || '';
        FIELD_HL_RE.lastIndex = 0;
        let m = FIELD_HL_RE.exec(text);
        while (m) {
          const r = new Range();
          r.setStart(node, m.index);
          r.setEnd(node, m.index + m[0].length);
          ranges.push(r);
          m = FIELD_HL_RE.exec(text);
        }
        node = walker.nextNode();
      }
      if (ranges.length) {
        CSS.highlights.set(name, new Highlight(...ranges));
      } else {
        CSS.highlights.delete(name);
      }
    });
  }, [highlightFields, testId]);

  // Keep editor DOM in sync when value changes from outside (e.g. Google Doc import)
  // ★ 2026-08-30 (code review) — il valore esterno viene sanificato (DOMPurify)
  // prima di finire in innerHTML: blocca script/handler in template importati.
  useEffect(() => {
    if (editorRef.current && !showHtml) {
      const safe = sanitizeHtml(value || '');
      // Only replace innerHTML if it's actually different to avoid caret jumps
      if (editorRef.current.innerHTML !== safe) {
        editorRef.current.innerHTML = safe;
      }
      // ★ Fase 8.V — riallinea l'evidenziazione dei {{campi}} al nuovo contenuto
      updateHighlights();
    }
    setRawHtml(value || '');
  }, [value, showHtml, updateHighlights]);

  // pulizia highlight allo smontaggio
  useEffect(() => () => {
    if (highlightRafRef.current) cancelAnimationFrame(highlightRafRef.current);
    if (typeof CSS !== 'undefined' && 'highlights' in CSS) {
      CSS.highlights.delete(`${testId}-fields`);
    }
  }, [testId]);

  const emit = useCallback(() => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    onChange?.(html);
    updateHighlights();
  }, [onChange, updateHighlights]);

  // ★ Fase 8.U — memorizza l'ultima posizione del cursore dentro l'editor
  useEffect(() => {
    const handler = () => {
      const sel = document.getSelection();
      if (!sel || sel.rangeCount === 0 || !editorRef.current) return;
      const range = sel.getRangeAt(0);
      if (editorRef.current.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
      }
    };
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, []);

  // ★ Fase 8.U — API imperativa per inserire testo al cursore (o in coda)
  useImperativeHandle(ref, () => ({
    insertText: (text) => {
      if (showHtml || !editorRef.current) return false;
      editorRef.current.focus();
      const sel = document.getSelection();
      if (!sel) return false;
      if (
        savedRangeRef.current
        && editorRef.current.contains(savedRangeRef.current.commonAncestorContainer)
      ) {
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current);
      } else {
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false); // caret alla fine
        sel.removeAllRanges();
        sel.addRange(range);
      }
      try {
        document.execCommand('insertText', false, text);
      } catch (err) {
        logger.warn('[RichEmailEditor] insertText failed:', err);
        return false;
      }
      emit();
      return true;
    },
    hasCursor: () => {
      const r = savedRangeRef.current;
      return !!(r && editorRef.current && editorRef.current.contains(r.commonAncestorContainer));
    },
  }), [showHtml, emit]);

  const exec = (cmd, arg = null) => {
    editorRef.current?.focus();
    try {
       
      document.execCommand(cmd, false, arg);
    } catch (err) {
       
      logger.warn('[RichEmailEditor] execCommand failed:', cmd, err);
    }
    emit();
  };

  const applyLink = () => {
    const url = (linkUrl || '').trim();
    if (!url) { setLinkPopoverOpen(false); return; }
    let safe = url;
    if (!/^(https?:|mailto:|tel:)/i.test(safe)) safe = `https://${safe}`;
    exec('createLink', safe);
    setLinkUrl('');
    setLinkPopoverOpen(false);
  };

  const applyImage = () => {
    const url = (imageUrl || '').trim();
    if (!url) { setImagePopoverOpen(false); return; }
    exec('insertImage', url);
    setImageUrl('');
    setImagePopoverOpen(false);
  };

  const applyHeading = (tag) => {
    exec('formatBlock', tag);
  };

  const clearFormatting = () => {
    exec('removeFormat');
    exec('formatBlock', 'p');
  };

  const toggleHtmlView = () => {
    if (showHtml) {
      // Applying raw HTML → back to WYSIWYG
      onChange?.(rawHtml);
      setShowHtml(false);
    } else {
      // Persist current HTML into rawHtml before switching
      setRawHtml(editorRef.current?.innerHTML || value || '');
      setShowHtml(true);
    }
  };

  const BtnGroup = ({ children }) => (
    <div className="flex items-center gap-0.5 px-1 border-r border-border last:border-r-0">{children}</div>
  );

  const IconBtn = ({ icon: Icon, label, onClick, active = false, testid }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`h-8 w-8 grid place-items-center rounded text-sm transition-colors ${active ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]' : 'text-slate-700 hover:bg-slate-100'}`}
      title={label}
      aria-label={label}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="border rounded-md bg-white" data-testid={testId}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center border-b border-border bg-slate-50 rounded-t-md py-1">
        <BtnGroup>
          <IconBtn icon={Bold} label={i18n.t('richEditor.bold', 'Grassetto')} onClick={() => exec('bold')} testid={`${testId}-bold`} />
          <IconBtn icon={Italic} label={i18n.t('richEditor.italic', 'Corsivo')} onClick={() => exec('italic')} testid={`${testId}-italic`} />
          <IconBtn icon={Underline} label={i18n.t('richEditor.underline', 'Sottolineato')} onClick={() => exec('underline')} testid={`${testId}-underline`} />
        </BtnGroup>
        <BtnGroup>
          <IconBtn icon={Heading1} label={i18n.t('richEditor.h1', 'Titolo H1')} onClick={() => applyHeading('h1')} testid={`${testId}-h1`} />
          <IconBtn icon={Heading2} label={i18n.t('richEditor.h2', 'Titolo H2')} onClick={() => applyHeading('h2')} testid={`${testId}-h2`} />
          <IconBtn icon={Heading3} label={i18n.t('richEditor.h3', 'Titolo H3')} onClick={() => applyHeading('h3')} testid={`${testId}-h3`} />
        </BtnGroup>
        <BtnGroup>
          <IconBtn icon={List} label={i18n.t('richEditor.ul', 'Elenco puntato')} onClick={() => exec('insertUnorderedList')} testid={`${testId}-ul`} />
          <IconBtn icon={ListOrdered} label={i18n.t('richEditor.ol', 'Elenco numerato')} onClick={() => exec('insertOrderedList')} testid={`${testId}-ol`} />
          <IconBtn icon={Quote} label={i18n.t('richEditor.quote', 'Citazione')} onClick={() => applyHeading('blockquote')} testid={`${testId}-quote`} />
        </BtnGroup>
        <BtnGroup>
          <IconBtn icon={AlignLeft} label={i18n.t('richEditor.alignLeft', 'Allinea a sinistra')} onClick={() => exec('justifyLeft')} testid={`${testId}-align-left`} />
          <IconBtn icon={AlignCenter} label={i18n.t('richEditor.alignCenter', 'Allinea al centro')} onClick={() => exec('justifyCenter')} testid={`${testId}-align-center`} />
          <IconBtn icon={AlignRight} label={i18n.t('richEditor.alignRight', 'Allinea a destra')} onClick={() => exec('justifyRight')} testid={`${testId}-align-right`} />
        </BtnGroup>
        <BtnGroup>
          <Popover open={linkPopoverOpen} onOpenChange={setLinkPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                className="h-8 w-8 grid place-items-center rounded text-slate-700 hover:bg-slate-100"
                title={i18n.t('richEditor.insertLink', 'Inserisci link')}
                aria-label={i18n.t('richEditor.insertLink', 'Inserisci link')}
                data-testid={`${testId}-link`}
              >
                <Link2 className="w-4 h-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 space-y-2" data-testid={`${testId}-link-popover`}>
              <Label className="text-xs">{i18n.t('richEditor.linkUrl', 'URL del link')}</Label>
              <Input
                autoFocus
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://…"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyLink(); } }}
              />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setLinkPopoverOpen(false)}>{i18n.t('common.cancel', 'Annulla')}</Button>
                <Button size="sm" onClick={applyLink}>{i18n.t('richEditor.apply', 'Applica')}</Button>
              </div>
            </PopoverContent>
          </Popover>

          <Popover open={imagePopoverOpen} onOpenChange={setImagePopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                className="h-8 w-8 grid place-items-center rounded text-slate-700 hover:bg-slate-100"
                title={i18n.t('richEditor.insertImage', 'Inserisci immagine')}
                aria-label={i18n.t('richEditor.insertImage', 'Inserisci immagine')}
                data-testid={`${testId}-image`}
              >
                <ImageIcon className="w-4 h-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 space-y-2" data-testid={`${testId}-image-popover`}>
              <Label className="text-xs">{i18n.t('richEditor.imageUrl', 'URL immagine')}</Label>
              <Input
                autoFocus
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://…jpg"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyImage(); } }}
              />
              <p className="text-[10px] text-muted-foreground">{i18n.t('richEditor.imageUrlHint', "Devi incollare un URL pubblico dell'immagine (Drive/Imgur/CDN).")}</p>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setImagePopoverOpen(false)}>{i18n.t('common.cancel', 'Annulla')}</Button>
                <Button size="sm" onClick={applyImage}>{i18n.t('richEditor.insert', 'Inserisci')}</Button>
              </div>
            </PopoverContent>
          </Popover>
        </BtnGroup>
        <BtnGroup>
          <IconBtn icon={Undo2} label={i18n.t('richEditor.undo', 'Annulla')} onClick={() => exec('undo')} testid={`${testId}-undo`} />
          <IconBtn icon={Redo2} label={i18n.t('richEditor.redo', 'Ripristina')} onClick={() => exec('redo')} testid={`${testId}-redo`} />
          <IconBtn icon={Eraser} label={i18n.t('richEditor.clearFormat', 'Rimuovi formattazione')} onClick={clearFormatting} testid={`${testId}-clear`} />
        </BtnGroup>
        <div className="ml-auto pr-1">
          <button
            type="button"
            onClick={toggleHtmlView}
            className={`h-8 px-2 grid place-items-center rounded text-xs transition-colors gap-1 flex items-center ${showHtml ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]' : 'text-slate-700 hover:bg-slate-100'}`}
            title={showHtml ? i18n.t('richEditor.toVisual', 'Torna a Editor visivo') : i18n.t('richEditor.toHtml', 'Mostra HTML raw')}
            data-testid={`${testId}-toggle-html`}
          >
            <Code className="w-4 h-4" />
            {showHtml ? i18n.t('richEditor.visual', 'Visuale') : 'HTML'}
          </button>
        </div>
      </div>

      {/* Editor area */}
      {showHtml ? (
        <textarea
          value={rawHtml}
          onChange={(e) => {
            setRawHtml(e.target.value);
            onChange?.(e.target.value);
          }}
          className="w-full p-3 text-xs font-mono resize-y outline-none rounded-b-md"
          style={{ minHeight }}
          placeholder="&lt;p&gt;HTML raw…&lt;/p&gt;"
          data-testid={`${testId}-html-textarea`}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label={placeholder}
          onInput={emit}
          onBlur={emit}
          className="prose prose-sm max-w-none p-3 outline-none rounded-b-md focus:ring-2 focus:ring-[hsl(var(--primary))]/20 focus:ring-inset"
          style={{ minHeight }}
          data-testid={`${testId}-content`}
          data-placeholder={placeholder}
        />
      )}

      <style>{`
        [data-testid="${testId}-content"]:empty::before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
        }
        [data-testid="${testId}-content"] p { margin: 0.5em 0; }
        [data-testid="${testId}-content"] ul,
        [data-testid="${testId}-content"] ol { margin: 0.5em 0 0.5em 1.5em; }
        [data-testid="${testId}-content"] h1 { font-size: 1.5em; font-weight: 700; margin: 0.6em 0 0.3em; color: #0C3C2D; }
        [data-testid="${testId}-content"] h2 { font-size: 1.3em; font-weight: 600; margin: 0.6em 0 0.3em; color: #0C3C2D; }
        [data-testid="${testId}-content"] h3 { font-size: 1.15em; font-weight: 600; margin: 0.5em 0 0.3em; color: #0C3C2D; }
        [data-testid="${testId}-content"] a { color: #0C3C2D; text-decoration: underline; }
        [data-testid="${testId}-content"] blockquote {
          border-left: 3px solid #D6B25E;
          padding-left: 12px;
          color: #4b5563;
          margin: 0.6em 0;
          font-style: italic;
        }
        [data-testid="${testId}-content"] img { max-width: 100%; height: auto; }
        [data-testid="${testId}-content"]::highlight(${testId}-fields) {
          background-color: rgba(214, 178, 94, 0.32);
          color: #0C3C2D;
          text-decoration: underline;
          text-decoration-color: rgba(214, 178, 94, 0.9);
          text-decoration-thickness: 2px;
        }
      `}</style>
    </div>
  );
});

export default RichEmailEditor;
